import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Divider, Drawer, Stack } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import { SerialPortConfigFields } from '@/features/controller/configuration/components/drawers/SerialPortConfigFields'
import { ToggleButtonGroupEnumConfigField } from '@/features/controller/configuration/components/drawers/ToggleButtonGroupEnumConfigField'
import type { SerialAndModbusConfig } from '@/features/controller/configuration/models/controllerConfigTree'
import { ModbusSerialEncoding } from '@/features/controller/configuration/models/raspiProperties/types'
import { getLegacySerialPortSchema } from '@/features/controller/configuration/schemas'
import type { ConfigDrawerProps, LegacySerialPortSchemaType } from '@/features/controller/configuration/types'

export function SerialPortSettingDrawer({
  config,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<ConfigDrawerProps<SerialAndModbusConfig>>) {
  const { t } = useTranslation()
  const form = useForm<LegacySerialPortSchemaType>({
    defaultValues: config,
    mode: 'onBlur',
    resolver: zodResolver(getLegacySerialPortSchema()),
  })

  const hasChanges = JSON.stringify(form.watch()) !== JSON.stringify(config)

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle title={t('controllers.configuration.device_connections.serial.dialog.title')} onClose={onClose} />

        <Divider />

        <DrawerDescription description={t('controllers.configuration.device_connections.serial.dialog.description')} />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {/* Serial port section */}
          <Stack direction="column" padding={2} spacing={2}>
            <SerialPortConfigFields
              portConfigFieldName="legacySerialPortConfig.portConfig"
              serialPortFieldName="legacySerialPortConfig.serialPort"
            />

            <Divider />

            {/* Modbus section */}
            <IntegerConfigField
              helpTooltip={t('controllers.configuration.device_connections.serial.dialog.modbus_device_number_tooltip')}
              label={t('controllers.configuration.device_connections.serial.dialog.modbus_device_number')}
              name="legacyModbusSerialConfig.deviceNumber"
              textFieldProps={{
                slotProps: { htmlInput: { min: 1 } },
              }}
            />
            <ToggleButtonGroupEnumConfigField
              enumLabels={{
                rtu: t('controllers.configuration.device_connections.serial.dialog.modbus_encoding_rtu'),
                ascii: t('controllers.configuration.device_connections.serial.dialog.modbus_encoding_ascii'),
                bin: t('controllers.configuration.device_connections.serial.dialog.modbus_encoding_bin'),
              }}
              enumType={ModbusSerialEncoding}
              label={t('controllers.configuration.device_connections.serial.dialog.modbus_encoding')}
              name="legacyModbusSerialConfig.encoding"
            />
            <IntegerConfigField
              helpTooltip="Multiple requests in a row are likely to fail on some devices if there is no delay."
              label={t('controllers.configuration.device_connections.serial.dialog.modbus_transmission_delay')}
              name="legacyModbusSerialConfig.transmissionDelayInMilliseconds"
              textFieldProps={{
                slotProps: { htmlInput: { min: 0 } },
              }}
              unit="milliseconds"
            />
          </Stack>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions
          hasChanges={hasChanges}
          onClose={onClose}
          onSave={(updatedConfig: LegacySerialPortSchemaType) => onConfigUpdated(updatedConfig)}
        />
      </FormProvider>
    </Drawer>
  )
}
